import { useDataProvider, useNotify } from 'react-admin';
import { useEffect, useState } from "react";
import { useAuth0 } from '@auth0/auth0-react';
import { buildAuthorizationHeader } from '../Commons/AuthHeaders';
import InputFieldCustom from '../Commons/InputFieldCustom'
import { useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import historyTracker from '../Commons/historyTracker';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import AuthNotification from '../Commons/AuthNotification';
import { Style } from '../Commons/Styles.jsx';
import BottomToolBar from '../Commons/BottomToolBar';
import InternalMenu from '../Commons/InternalMenu';
import States from '../Commons/States';
// TODO: reemplazar por el ícono definitivo de Bancos cuando esté disponible
import banks from '../Commons/Icons/building.svg';
import requestAuthorization from '../Commons/Icons/colocacion.svg';
// TODO: reemplazar por el ícono definitivo de Imprimir Solicitudes cuando esté disponible
import printRequests from '../Commons/Icons/contabilidad.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// Igual que App.jsx (myDataProvider.delete/auth): domain propio para poder armar un fetch()
// crudo. Hace falta aca porque bank/printrequests/{id}/reprint ahora devuelve el PDF binario
// (application/pdf), y dataProvider (ra-data-simple-rest) solo sabe parsear JSON.
const domain = process.env.REACT_APP_URL_API ?? '';

// Mismos catalogos que RequestAuthorizationShow.jsx (misma SP de detalle, mismos valores
// crudos de tipo_pago/codigo_tipo_mov/estatus).
const PaymentTypeNames = {
    0: 'Cliente',
    1: 'Proveedor',
    2: 'Otro',
    3: 'Cuenta Corriente',
    4: 'Remesa',
};

const MovementTypeNames = {
    1: 'Cheque',
    2: 'Deposito',
    3: 'Nota de Debito',
    4: 'Nota de Credito',
};

const StatusNames = {
    0: 'Nueva',
    1: 'Por Autorizar',
    2: 'Autorizada',
    3: 'Ejecutada',
    4: 'Rechazada',
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString('es-GT') : '';

const formatMoney = (value) =>
    value === null || value === undefined
        ? ''
        : parseFloat(value).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 });

// Detalle de UNA solicitud pendiente de imprimir/reimprimir. El "id" es simplemente el
// numero_solicitud (ya no un compuesto "codigo_cuenta-numero_solicitud" -- ver comentario
// de SP_SolicitudImpresion en Models.cs). El detalle se trae de Get_Bank_PrintRequest
// (sp_get_cbmaesolicitud_PIMP), que NO filtra por estatus, asi que sirve tanto para una
// solicitud estatus=2 (Autorizada, pendiente de imprimir) como estatus=3 (ya Ejecutada,
// para reimprimir). Se valida el permiso "BankPrintRequest" (el mismo que
// RequestAuthorizationsList.jsx), no "BankRequestAuthorization", para no depender de que
// el usuario tenga permisos de autorizar/rechazar solo para poder ver/imprimir esta
// solicitud.
//
// Acciones:
// - "Imprimir" (estatus=2): pide confirmacion (igual que "Autorizar" en
//   RequestAuthorizationShow.jsx no lo hace, pero aca SI hace falta porque
//   sp_cbprocesoimpresioncheque/sp_cbprocesoNDNC asientan contabilidad real y NO se pueden
//   reintentar), llama a Bank_PrintRequest_Execute (bank/printrequests/{id}/print) -- que
//   corre la SP de proceso correspondiente segun movementTypeCode -- y si sale bien
//   inmediatamente descarga el PDF (ver downloadPrintDocument).
// - "Reimprimir" (estatus=3): sin confirmacion (no asienta nada, es de solo lectura) --
//   descarga el mismo PDF llamando directo a Bank_PrintRequest_Reprint
//   (bank/printrequests/{id}/reprint), que corre sp_cbinfoimpresioncheque y arma el
//   documento con ChequePdfGenerator (coordenadas extraidas de ChequeBI.jasper). Pensado
//   para cuando se traba la impresora despues de un "Imprimir" exitoso.
const PrintRequestShow = () => {
    const { id } = useParams();
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();

    const [openAuth, setOpenAuth] = useState(false);
    const [auth, setAuth] = useState(false);
    const [authPrint, setAuthPrint] = useState(false);
    const [authReprint, setAuthReprint] = useState(false);
    const [account, setAccount] = useState('');
    const [requestNumber, setRequestNumber] = useState('');
    const [documentDate, setDocumentDate] = useState('');
    const [promisedPaymentDate, setPromisedPaymentDate] = useState('');
    const [paymentType, setPaymentType] = useState('');
    const [movementType, setMovementType] = useState('');
    const [payableTo, setPayableTo] = useState('');
    const [amount, setAmount] = useState('');
    const [amountML, setAmountML] = useState('');
    const [referenceRate, setReferenceRate] = useState('');
    const [concept, setConcept] = useState('');
    const [requestDate, setRequestDate] = useState('');
    const [moduleCode, setModuleCode] = useState('');
    const [status, setStatus] = useState('');
    const [user, setUser] = useState('');
    const [movementDate, setMovementDate] = useState('');
    const [lastCheckNumber, setLastCheckNumber] = useState(null);

    const [statusCode, setStatusCode] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [reprinting, setReprinting] = useState(false);
    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
    // "initialLoading" en el return): esta pantalla necesita traer permisos y el detalle
    // de la solicitud antes de poder mostrarlo, y sin este estado la ventana se veia en
    // blanco (solo el breadcrumb) durante esa espera.
    const [initialLoading, setInitialLoading] = useState(true);

    const loadRequest = async () => {
        const { data } = await dataProvider.getOne('bank/printrequests', { id });

        historyTracker.addPath(location.pathname, `Solicitud ${data.requestNumber || ''}`, 3);

        setAccount(data.accountNumber ? [data.accountNumber, data.accountName].filter(Boolean).join(' - ') : '');
        setRequestNumber(data.requestNumber?.toString() || '');
        setDocumentDate(formatDate(data.documentDate));
        setPromisedPaymentDate(formatDate(data.promisedPaymentDate));
        setPaymentType(PaymentTypeNames[data.paymentType] || '');
        setMovementType(MovementTypeNames[data.movementTypeCode] || '');
        setPayableTo(data.payableTo || '');
        setAmount(formatMoney(data.amount));
        setAmountML(formatMoney(data.amountML));
        setReferenceRate(data.referenceRate !== null && data.referenceRate !== undefined ? parseFloat(data.referenceRate).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : '');
        setConcept(data.concept || '');
        setRequestDate(formatDate(data.requestDate));
        setModuleCode(data.moduleCode || '');
        setStatus(StatusNames[data.status] || '');
        setUser(data.user || '');
        setMovementDate(formatDate(data.movementDate));
        setStatusCode(data.status);
        // ultimo_cheque_proc de cbtaban (el numero del ultimo cheque ya procesado en esta
        // cuenta bancaria), via sp_get_cbmaesolicitud_PIMP -- ver LastCheckNumber en
        // SP_SolicitudImpresion (Models.cs). Es una nota informativa, igual que "Saldo
        // pendiente por registrar" en AccountingExpenseCreate.jsx: ayuda a confirmar que el
        // talonario cargado en la impresora coincide con la secuencia esperada antes de
        // imprimir un cheque nuevo.
        setLastCheckNumber(data.lastCheckNumber || null);
    };

    useEffect(() => {
        (async () => {
            try {
                // Permiso "BankPrintRequest" (el mismo que RequestAuthorizationsList.jsx),
                // no "BankRequestAuthorization" -- es a proposito distinto, para que ver el
                // detalle de una solicitud a imprimir no dependa de tener permisos sobre
                // Autorizar Solicitudes. Mismo patron de RequestAuthorizationShow.jsx
                // (action: "list,authorize,reject"): se piden las 3 acciones de una vez --
                // authdata.data[0]/[1]/[2] son list/print/reprint en ese orden -- para que
                // "Imprimir"/"Reimprimir" no queden habilitados solo por tener acceso de
                // consulta a la pantalla.
                const authdata = { data: hasPermissions('BankPrintRequest', 'list,print,reprint') };
                if (authdata.data.length > 0) {
                    setAuth(authdata.data[0]);
                    setOpenAuth(!authdata.data[0]);
                    setAuthPrint(authdata.data[1]);
                    setAuthReprint(authdata.data[2]);

                    if (authdata.data[0]) {
                        await loadRequest();
                    }
                } else {
                    setAuth(false);
                    setOpenAuth(true);
                }
            }
            catch (error) {
                console.log(error);
            }
            finally {
                setInitialLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataProvider, location.pathname, id]);

    const handleOpenPrintDialog = () => setPrintDialogOpen(true);
    const handleClosePrintDialog = () => setPrintDialogOpen(false);

    // bank/printrequests/{id}/reprint devuelve el PDF ya armado (application/pdf, ver
    // Bank_PrintRequest_Reprint/ChequePdfGenerator.cs en el backend) -- no JSON, asi que no se
    // puede usar dataProvider (ra-data-simple-rest solo sabe parsear JSON). Se arma un fetch()
    // crudo con los mismos headers que ya usa myDataProvider.delete/auth en App.jsx, y se
    // descarga el blob resultante via un <a download> temporal. Es de solo lectura (no asienta
    // nada), asi que sirve tanto para "Reimprimir" como para bajar el documento justo despues
    // de un "Imprimir" exitoso.
    const downloadPrintDocument = async (requestNum) => {
        const c = await getIdTokenClaims();
        const response = await fetch(`${domain}/bank/printrequests/${requestNum}/reprint`, {
            method: 'GET',
            headers: {
                user: c?.sub,
                username: c?.nickname,
                company: States.getCompany(),
                organization: States.getOrganizationId(),
                nit: States.getNIT(),
                ...(await buildAuthorizationHeader(getAccessTokenSilently)),
            },
        });
        if (!response.ok) {
            let message = 'No se pudo generar el documento.';
            try {
                const errorJson = await response.json();
                message = errorJson.error || errorJson.message || message;
            } catch {
                // La respuesta de error tambien es JSON (ver Bank_PrintRequest_Reprint) salvo
                // que ni siquiera haya llegado a la Function (404 crudo, etc.) -- se deja el
                // mensaje generico en ese caso.
            }
            throw new Error(message);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Cheque_${requestNum}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleConfirmPrint = async () => {
        setPrintDialogOpen(false);
        setPrinting(true);
        try {
            // Bank_PrintRequest_Execute es "post" (asienta contabilidad real), asi que se
            // llama con dataProvider.create() -- igual que Autorizar/Rechazar en
            // RequestAuthorizationShow.jsx -- y no con getOne() (que manda GET y no matchea
            // la ruta del backend).
            await dataProvider.create(`bank/printrequests/${id}/print`, { data: {} });
            await downloadPrintDocument(id);
            notify('Solicitud impresa correctamente.', { type: 'success' });
            await loadRequest();
        } catch (error) {
            notify(error?.message || 'Ocurrio un error al imprimir la solicitud.', { type: 'error' });
        } finally {
            setPrinting(false);
        }
    };

    const handleReprint = async () => {
        setReprinting(true);
        try {
            await downloadPrintDocument(id);
            notify('Documento generado correctamente.', { type: 'success' });
        } catch (error) {
            notify(error?.message || 'Ocurrio un error al reimprimir la solicitud.', { type: 'error' });
        } finally {
            setReprinting(false);
        }
    };

    const handleRedirectRequestAuthorizations = () => {
        navigate(`/bank/requestauthorization`);
    };

    const handleRedirectPrintRequests = () => {
        navigate(`/bank/printrequests`);
    };

    const [openBanks, setOpenBanks] = useState(true);

    const handleClickOpenBanks = () => {
        setOpenBanks(!openBanks);
    };

    const menus = [{
        icon: <img src={banks} alt="logo" style={Style.MenuLogo} />, name: 'Bancos',
        open: openBanks,
        openAction: handleClickOpenBanks,
        actions: [
            { icon: <img src={requestAuthorization} alt="logo" style={Style.MenuLogo} />, name: 'Autorizar Solicitudes', onClick: handleRedirectRequestAuthorizations },
            { icon: <img src={printRequests} alt="logo" style={Style.MenuLogo} />, name: 'Imprimir Solicitudes', onClick: handleRedirectPrintRequests },
        ]
    }];

    const buttons = [
        { auth: authPrint && statusCode === 2, action: handleOpenPrintDialog, name: printing ? 'Imprimiendo...' : 'Imprimir' },
        { auth: authReprint && statusCode === 3, action: handleReprint, name: reprinting ? 'Reimprimiendo...' : 'Reimprimir' },
    ];

    return (
        <div class="container">
            <BreadCrumbCustom history={historyTracker.getHistory()} />
            {initialLoading ? (
                // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
                // el mismo CircularProgress que se usa mas abajo para el overlay de imprimir/
                // reimprimir, pero aqui cubre la espera de auth + detalle de la solicitud, que
                // antes dejaba la pantalla en blanco.
                <div class="Loading" style={{ display: 'block' }}>
                    <CircularProgress size="15rem" />
                </div>
            ) : auth ? (
                <div class="edit">
                    {!isSmall ? (
                        <div class="container">
                            {lastCheckNumber ? (
                                <div class="row">
                                    <div class="col-12">
                                        <label style={Style.Label}>
                                            Cheque actual de esta cuenta: {lastCheckNumber}
                                        </label>
                                    </div>
                                </div>
                            ) : null}
                            <div class="row">
                                <InputFieldCustom readOnly value={requestNumber} setValue={setRequestNumber} col="col-3" width="95%" label="No. Solicitud" type="text" />
                                <InputFieldCustom readOnly value={account} setValue={setAccount} col="col-3" width="95%" label="Cuenta Bancaria" type="text" />
                                <InputFieldCustom readOnly value={movementType} setValue={setMovementType} col="col-3" width="95%" label="Tipo" type="text" />
                                <InputFieldCustom readOnly value={paymentType} setValue={setPaymentType} col="col-3" width="95%" label="Tipo de Pago" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={status} setValue={setStatus} col="col-3" width="95%" label="Estado" type="text" />
                                <InputFieldCustom readOnly value={documentDate} setValue={setDocumentDate} col="col-3" width="95%" label="Fecha Documento" type="text" />
                                <InputFieldCustom readOnly value={promisedPaymentDate} setValue={setPromisedPaymentDate} col="col-3" width="95%" label="Fecha Promesa de Pago" type="text" />
                                <InputFieldCustom readOnly value={requestDate} setValue={setRequestDate} col="col-3" width="95%" label="Fecha Solicitud" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={payableTo} setValue={setPayableTo} col="col-3" width="95%" label="Girado" type="text" />
                                <InputFieldCustom readOnly value={amount} setValue={setAmount} col="col-3" width="95%" label="Monto" type="text" />
                                <InputFieldCustom readOnly value={amountML} setValue={setAmountML} col="col-3" width="95%" label="Monto Moneda Local" type="text" />
                                <InputFieldCustom readOnly value={referenceRate} setValue={setReferenceRate} col="col-3" width="95%" label="Tasa de Referencia" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={moduleCode} setValue={setModuleCode} col="col-3" width="95%" label="Módulo" type="text" />
                                <InputFieldCustom readOnly value={user} setValue={setUser} col="col-3" width="95%" label="Usuario" type="text" />
                                <InputFieldCustom readOnly value={movementDate} setValue={setMovementDate} col="col-3" width="95%" label="Fecha de Movimiento" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={concept} setValue={setConcept} col="col-12" width="95%" label="Concepto" type="text" component="textarea" referenceCol="col-3" />
                            </div>

                            <div style={{ marginTop: '2em' }}>
                                <BottomToolBar buttons={buttons} menu={<InternalMenu menus={menus} />} />
                            </div>
                        </div>
                    ) : (
                        <div class="container">
                        </div>
                    )}

                    {/* DialogTitle/DialogContent de MUI envuelven su contenido en su propio
                        Typography, que usa la fuente por defecto del tema (Roboto) en vez de
                        heredar el "Gothic" del proyecto (definido a nivel body/h1-h6 en
                        index.css) -- por eso hace falta fijar fontFamily explicito aca, igual
                        que hace el resto de la pantalla via Style.jsx. */}
                    <Dialog open={printDialogOpen} onClose={handleClosePrintDialog}>
                        <DialogTitle style={{ fontFamily: 'BoldGothic', color: '#191268' }}>Confirmar Impresión</DialogTitle>
                        <DialogContent style={{ fontFamily: 'Gothic' }}>
                           ¿Desea continuar con la impresion de la
                            solicitud No. {requestNumber}?
                        </DialogContent>
                        <DialogActions>
                            {/* Style.DeleteButton/SaveButton traen width: '100%' -- pensado
                                para el col-2 de BottomToolBar -- asi que aca se pisa a 'auto'
                                para que los botones no se estiren a todo el ancho del dialog. */}
                            <Button style={{ ...Style.DeleteButton, width: 'auto' }} onClick={handleClosePrintDialog}>Cancelar</Button>
                            <Button style={{ ...Style.SaveButton, width: 'auto' }} onClick={handleConfirmPrint} autoFocus>Confirmar</Button>
                        </DialogActions>
                    </Dialog>

                    {(printing || reprinting) && (
                        <div style={{
                            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 2000,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <CircularProgress />
                        </div>
                    )}
                </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)
            }
        </div>
    )
}

export default PrintRequestShow;
